// Story 9.4 (ADO-1948): the shared Money formatter for apps/web.
//
// The API hands every monetary value over as the `@shoplink/domain` Money wire
// shape, `{ amount, currency }` (never a bare number: the repo Money rule). This is
// the single place that turns one into display text, so every screen renders a
// price the same way and none of them re-implements currency handling.
//
// `Intl.NumberFormat` is expensive to construct and an order list formats two
// values per row on every render, so a formatter is built once per currency and
// cached (the hoisting note in `app/Browse/productCardSample.ts`). The cache is
// keyed by the UPPER-CASED code, so `eur` and `EUR` share one instance.
//
// The currency is whatever the DTO carries: nothing here assumes EUR.

/** The wire shape of `@shoplink/domain` Money, as the API serialises it. */
export interface MoneyValue {
  readonly amount: number;
  readonly currency: string;
}

/** A wire value is usable Money only with a finite numeric amount and a string
 *  currency. Shared by the API clients' response parsers. */
export function isMoneyValue(value: unknown): value is MoneyValue {
  if (typeof value !== 'object' || value === null) return false;
  const { amount, currency } = value as Partial<MoneyValue>;
  return typeof amount === 'number' && Number.isFinite(amount) && typeof currency === 'string';
}

/**
 * Display locale. Irish English: the platform's tenants trade in Ireland, and it
 * gives the grouping/decimal separators the SL1.0 screens used. The CURRENCY is
 * never taken from here, only the number shaping.
 */
const DISPLAY_LOCALE = 'en-IE';

/** Shown in place of an amount that is not a finite number (contract drift). */
const UNAVAILABLE = 'Unavailable';

const formatterCache = new Map<string, Intl.NumberFormat | null>();

/**
 * The cached `Intl.NumberFormat` for `currency`, or `null` when the code is not one
 * `Intl` accepts (contract drift, an empty column). Exported so the caching contract
 * is directly testable and so a future screen can reuse the same instance.
 */
export function moneyFormatterFor(currency: string): Intl.NumberFormat | null {
  const code = currency.trim().toUpperCase();
  const cached = formatterCache.get(code);
  if (cached !== undefined) return cached;
  let formatter: Intl.NumberFormat | null;
  try {
    formatter = new Intl.NumberFormat(DISPLAY_LOCALE, { style: 'currency', currency: code });
  } catch {
    // Intl throws a RangeError on a code that is not a well-formed ISO 4217 value.
    formatter = null;
  }
  formatterCache.set(code, formatter);
  return formatter;
}

/**
 * Render a Money value for display, e.g. `€86.88`.
 *
 * Degrades rather than throws, because one bad row must not white-screen a list: an
 * unusable currency code falls back to the amount plus the raw code, and a
 * non-finite amount falls back to a stated placeholder.
 */
export function formatMoney(money: MoneyValue): string {
  if (!Number.isFinite(money.amount)) return UNAVAILABLE;
  const formatter = moneyFormatterFor(money.currency);
  if (formatter === null) return `${money.amount.toFixed(2)} ${money.currency}`;
  return formatter.format(money.amount);
}
